import { Alert, Button, Card, Drawer, Empty, List, Select, Space, Spin, Table, Tag, Tooltip, notification } from 'antd'
import { HistoryOutlined, PlusOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TemplateBuilder from '../../shared/template-builder'
import ApiService from '../../core/service/api-service'
import { TemplateLanguages } from '../../enums/TemplateLanguages'
import { defaultReportOutputTemplate } from './defaultReportOutputTemplate'

/**
 * Hastane rapor çıktı şablonu builder ekranı.
 *
 * Motor sürümü (engine_version) satır bazındadır:
 *  - v1 → şablon hâlâ yüklenmiş .docx dosyasından üretilir; builder'da kaydedilene kadar öyle kalır.
 *  - v2 → tanım DB'de; çıktı /v2/Template/Render ile üretilir.
 * Kaydetmek şablonu v2'ye geçirir. Word yüklemek (hastane ekranındaki mevcut menü) v1'e geri döndürür.
 */
export default function ReportOutputTemplateBuilder({ hospitalId }) {
    const { t } = useTranslation()

    const [loading, setLoading] = useState(false)
    const [templates, setTemplates] = useState([])
    const [placeholders, setPlaceholders] = useState([])
    const [selectedId, setSelectedId] = useState(null)
    const [language, setLanguage] = useState('tr')
    const [definition, setDefinition] = useState(null)
    const [revisions, setRevisions] = useState([])
    const [revisionsOpen, setRevisionsOpen] = useState(false)
    // Şablon açıkken liste kalıcı bir kolon olarak durunca editöre ekranın çok azı kalıyordu
    // (dış liste %25 + iç palet/önizleme → editör ~%37). Liste artık çekmeceye taşınıyor.
    const [listOpen, setListOpen] = useState(false)

    const selected = useMemo(
        () => templates.find((x) => x.id === selectedId) || null,
        [templates, selectedId]
    )

    const loadTemplates = useCallback(async () => {
        if (!hospitalId) return
        setLoading(true)
        try {
            const res = await ApiService.ReportOutputTemplate.ListTemplates(hospitalId)
            if (res.success) setTemplates(res.data || [])
        } finally {
            setLoading(false)
        }
    }, [hospitalId])

    useEffect(() => { loadTemplates() }, [loadTemplates])

    useEffect(() => {
        ApiService.ReportOutputTemplate.Placeholders().then((res) => {
            if (res.success) setPlaceholders(res.data || [])
        })
    }, [])

    const openTemplate = async (template) => {
        setSelectedId(template.id)
        setLanguage(template.language || 'tr')
        setListOpen(false)

        const res = await ApiService.ReportOutputTemplate.GetDefinition(hospitalId, template.id)
        if (!res.success) return

        const raw = res.data?.definition
        if (raw) {
            try {
                setDefinition(JSON.parse(raw))
                return
            } catch (e) {
                notification.error({ message: t('Şablon tanımı okunamadı') })
            }
        }

        // v1 şablonu: builder'da yeniden kurulur, mevcut .docx bozulmaz. Boş sayfa yerine hazır
        // iskeletle başlanır — kaydedilmediği sürece eski çıktı aynen üretilmeye devam eder.
        const lang = template.language || 'tr'
        setDefinition(defaultReportOutputTemplate([lang], lang))
    }

    // Boş sayfa yerine hazır iskelet: antet, hasta/çekim tablosu, dört rapor bölümü, imza.
    // Kullanıcı her bloğu tek tek kurmak yerine üzerinde düzenler.
    const createNew = () => {
        setSelectedId(null)
        setListOpen(false)
        setDefinition(defaultReportOutputTemplate([language], language))
    }

    const save = async (def) => {
        const res = await ApiService.ReportOutputTemplate.SaveDefinition(hospitalId, {
            templateId: selectedId || null,
            language: def.defaultLanguage || language,
            name: def.name,
            definition: JSON.stringify(def),
        })

        if (res.success) {
            setSelectedId(res.data)
            await loadTemplates()
        }
    }

    const preview = async (def, lang) => {
        const res = await ApiService.ReportOutputTemplate.PreviewDefinition(hospitalId, {
            definition: JSON.stringify(def),
            language: lang,
        })

        if (!res.success) throw new Error(res.message || t('Önizleme üretilemedi'))
        return res.data
    }

    const openRevisions = async () => {
        if (!selectedId) return
        const res = await ApiService.ReportOutputTemplate.Revisions(hospitalId, selectedId)
        if (res.success) {
            setRevisions(res.data || [])
            setRevisionsOpen(true)
        }
    }

    const restore = async (revisionId) => {
        const res = await ApiService.ReportOutputTemplate.RestoreRevision(hospitalId, revisionId)
        if (res.success) {
            setRevisionsOpen(false)
            await loadTemplates()
            const template = templates.find((x) => x.id === selectedId)
            if (template) await openTemplate(template)
        }
    }

    // Liste hem tam ekran (şablon seçilmeden) hem çekmece içinde (şablon açıkken) kullanılıyor.
    const templateList = (
        <Spin spinning={loading}>
            {templates.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Şablon yok')} />
            ) : (
                <Table
                    size="small"
                    pagination={false}
                    rowKey="id"
                    dataSource={templates}
                    onRow={(row) => ({ onClick: () => openTemplate(row) })}
                    rowClassName={(row) => (row.id === selectedId ? 'ant-table-row-selected' : '')}
                    columns={[
                        {
                            title: t('Dil'),
                            dataIndex: 'language',
                            width: 60,
                            render: (value) => (value || '').toUpperCase(),
                        },
                        {
                            title: t('Ad'),
                            dataIndex: 'name',
                            render: (value, row) => value || <span style={{ color: '#999' }}>#{row.id}</span>,
                        },
                        {
                            title: t('Motor'),
                            dataIndex: 'engineVersion',
                            width: 80,
                            render: (value) =>
                                value >= 2 ? (
                                    <Tooltip title={t('Builder tanımından üretiliyor')}>
                                        <Tag color="green">v2</Tag>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title={t('Yüklenmiş Word dosyasından üretiliyor')}>
                                        <Tag>v1</Tag>
                                    </Tooltip>
                                ),
                        },
                    ]}
                />
            )}
        </Spin>
    )

    const listActions = (
        <Space>
            <Select
                size="small"
                style={{ width: 90 }}
                value={language}
                onChange={setLanguage}
                options={TemplateLanguages.map((code) => ({ value: code, label: code.toUpperCase() }))}
            />
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={createNew}>
                {t('Yeni')}
            </Button>
        </Space>
    )

    return (
        <>
            {!definition ? (
                <Card size="small" title={t('Çıktı Şablonları')} extra={listActions}>
                    {templateList}
                </Card>
            ) : (
                <>
                    {/* Şablon açıkken liste kolonu kalkıyor: editör tüm genişliği kullanır. */}
                    <Space style={{ marginBottom: 8 }} wrap>
                        <Button size="small" icon={<UnorderedListOutlined />} onClick={() => setListOpen(true)}>
                            {t('Şablonlar')}
                        </Button>
                        <Tag>{(selected?.language || language || '').toUpperCase()}</Tag>
                        <strong>{selected?.name || definition.name || t('Yeni şablon')}</strong>
                        {selected && (
                            <Tag color={selected.engineVersion >= 2 ? 'green' : undefined}>
                                v{selected.engineVersion}
                            </Tag>
                        )}
                        <Button size="small" icon={<HistoryOutlined />} onClick={openRevisions} disabled={!selectedId}>
                            {t('Revizyonlar')}
                        </Button>
                    </Space>

                    {selected && selected.engineVersion < 2 && (
                        <Alert
                            type="info"
                            showIcon
                            closable
                            style={{ marginBottom: 8 }}
                            message={t('Bu şablon hâlâ yüklenmiş Word dosyasından üretiliyor')}
                            description={t('Burada kaydettiğinizde builder motoruna (v2) geçer. Kaydetmediğiniz sürece mevcut çıktı değişmez.')}
                        />
                    )}

                    <TemplateBuilder
                        value={definition}
                        onChange={setDefinition}
                        placeholders={placeholders}
                        languages={TemplateLanguages}
                        defaultLanguage={language}
                        onSave={save}
                        onPreview={preview}
                        t={t}
                    />
                </>
            )}

            <Drawer
                title={t('Çıktı Şablonları')}
                open={listOpen}
                onClose={() => setListOpen(false)}
                width={560}
                extra={listActions}
            >
                {templateList}
            </Drawer>

            <Drawer
                title={t('Revizyonlar')}
                open={revisionsOpen}
                onClose={() => setRevisionsOpen(false)}
                width={420}
            >
                <List
                    dataSource={revisions}
                    renderItem={(item) => (
                        <List.Item
                            actions={[
                                <Button key="restore" size="small" onClick={() => restore(item.id)}>
                                    {t('Geri Yükle')}
                                </Button>,
                            ]}
                        >
                            <List.Item.Meta
                                title={`#${item.revision}`}
                                description={
                                    <>
                                        <div>{new Date(item.createdDate).toLocaleString()}</div>
                                        {item.note && <div>{item.note}</div>}
                                    </>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Drawer>
        </>
    )
}
